<template>
  <section class="content white large-padding">
    <form class="hide-for-print" @submit.prevent="handleSubmit" spellcheck="false">
      <TheLoader :active="true" v-if="loading"/>

      <fieldset style="margin: 0;">
        <div class="grid-x grid-padding-x">
          <TheSelectInput
            v-model="form.avdelingsid"
            containerClassName="cell large-2"
            label="Avdeling"
            name="Avdelingsid"
            :items="avdelingOpts"
            :disabled="loading"
          />

          <div class="cell large-6">
            <DateFromTillForm
              v-model="form.dates"
              :labelFrom="'Dato f.o.m'"
              :labelTill="'Dato t.o.m'"
              :requiredFrom="true"
              :requiredTill="true"
            />
          </div>
          <div class="cell auto">
            <label>&nbsp;</label>
            <button v-if="submittable" class="button primary">Ok</button>
            <button v-else-if="fetching" class="button primary loading">Ok <span class="loader-spinner"></span></button>
            <button v-else class="button primary disabled">Ok</button>
          </div>
        </div>
      </fieldset>
    </form>

    <div class="content-container flex-column">
      <div class="content-container flex-row content-x-scroll content-y-scroll table-results">
        <table class="large fixed sortable sticky-table-header compressed color-rows" style="height: 0">
          <thead>
            <tr>
              <th class="align-left unsortable">Mottaker</th>
              <th class="align-center unsortable">Kontrakter</th>
              <th class="align-center unsortable">Solgte obj.</th>
              <th class="align-center unsortable">Vurd. (lav)</th>
              <th class="align-center unsortable">Vurd. (høy)</th>
              <th class="align-center unsortable">Tilslag</th>
              <th class="align-center unsortable">Snitt salær</th>
              <th class="align-center unsortable">Solgt første gang</th>
              <th class="align-right unsortable">Totalt solgt</th>
            </tr>
          </thead>
          <tbody class="empty-container" v-if="items.length < 1">
            <tr>
              <td colspan="6">
                <em v-if="searched">Fant ingen data...</em>
                <em v-else>Søk med fra/til dato...</em>
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr v-for="item in items" :key="item.Brukernavn">
              <td>{{ item.Kontraktmottaker || item.Brukernavn }}</td>
              <td class="align-center">{{ item.kontrakter | thousands }}</td>
              <td class="align-center">{{ item.objekter | thousands }}</td>
              <td class="align-center">{{ item.vurderinglav | thousands }}</td>
              <td class="align-center">{{ item.vurderinghoy | thousands }}</td>
              <td class="align-center">{{ item.salgspris | thousands }}</td>
              <td class="align-center">{{ item.snittSalaer | thousands }}</td>
              <td class="align-center">{{ ((item.firstSellingObject * 100) / item.totalObjects) | thousandsFloat }} %</td>
              <td class="align-right">{{ ((item.totalObjectsSold * 100) / item.totalObjects) | thousandsFloat }} %</td>
            </tr>
            <template v-if="items.length > 0">
              <tr class="footer">
                <td class="align-left unsortable">Sum</td>
                <td class="align-center unsortable">{{ sumKontrakter | thousands }}</td>
                <td class="align-center unsortable">{{ sumObjekter | thousands }}</td>
                <td class="align-center unsortable">{{ sumVurderingLav | thousands }}</td>
                <td class="align-center unsortable">{{ sumVurderingHoy | thousands }}</td>
                <td class="align-center unsortable">{{ sumSalgspris | thousands }}</td>
                <td class="align-center unsortable"></td>
                <td class="align-center unsortable"></td>
                <td class="align-right unsortable"></td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<script>
import DateFromTillForm from '@/components/Inputs/DateFromTillForm'
import StatistikkService from '@/services/StatistikkService'
import InstillingerService from '@/services/InstillingerService'

export default {
  name: 'StatsSalgEksperterProsentandel',
  $_veeValidate: {
    validator: 'new'
  },
  components: {
    DateFromTillForm
  },
  data () {
    return {
      loading: false,
      fetching: false,
      searched: false,
      avdelingOpts: [],
      items: [],
      form: {
        avdelingsid: null,
        dates: {
          from: null,
          till: null
        }
      }
    }
  },
  computed: {
    submittable: function () {
      return !this.loading && !this.fetching && this.form.dates.from && this.form.dates.till
    },
    sumKontrakter: function () {
      return this.items.length < 1 ? 0 : this.items.reduce((acc, curr) => Number(acc) + Number(curr.kontrakter), 0)
    },
    sumObjekter: function () {
      return this.items.length < 1 ? 0 : this.items.reduce((acc, curr) => Number(acc) + Number(curr.objekter), 0)
    },
    sumVurderingLav: function () {
      return this.items.length < 1 ? 0 : this.items.reduce((acc, curr) => Number(acc) + Number(curr.vurderinglav), 0)
    },
    sumVurderingHoy: function () {
      return this.items.length < 1 ? 0 : this.items.reduce((acc, curr) => Number(acc) + Number(curr.vurderinghoy), 0)
    },
    sumSalgspris: function () {
      return this.items.length < 1 ? 0 : this.items.reduce((acc, curr) => Number(acc) + Number(curr.salgspris), 0)
    }
  },
  mounted () {
    this.fetchSettings()
  },
  methods: {
    handleSubmit: function () {
      if (!this.submittable) {
        return
      }

      this.searched = false
      this.fetching = true
      StatistikkService.loadExpertsJson(this.form.dates.from, this.form.dates.till, this.form.avdelingsid)
        .then(response => {
          this.items = response.data
        })
        .catch(() => {
          this.items = []
        })
        .then(() => {
          this.fetching = false
          this.searched = true
        })
    },
    fetchSettings: function () {
      this.loading = true

      this.avdelingOpts.unshift({key: null, val: 'Laster...'})

      InstillingerService('avdeling').dropdown()
        .then((response) => {
          this.avdelingOpts = response.data
          this.avdelingOpts.unshift({ key: null, val: 'Alle' })
        })
        .catch(() => {
          // Skip error
          this.$helpers.showErrorAlert('Feil', 'Kunne ikke laste ned avdeling innstillinger')
        })
        .then(() => {
          this.loading = false
        })
    }
  }
}
</script>
<style scoped>
  table.fixed tbody.empty-container tr td:after {
    border: none;
    background-color: revert;
  }

  table.fixed tbody tr.footer td {
    font-weight: bold;
  }
</style>



// WEBPACK FOOTER //
// src/components/Admin/Reports/Stats/StatsSalgEksperterProsentandel.vue